import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  cleanCnpj,
  formatCnpj,
  formatCpf,
  formatPhone,
  isValidCnpj,
  isValidCpf,
  isValidPhone,
} from "../../entrego/pages/saldo-consulta/utils/format";
import { offerTrackingService } from "../services/offer-tracking-service";
import {
  IndividualResult,
  LastStageFilter,
  OpsFilter,
  OverviewResult,
  SearchFormData,
} from "../types";

export const offerTrackingKeys = {
  all: ["offer-tracking"] as const,
  overview: (
    dbName: string,
    ops: string,
    date: string,
    lastStage: string,
    page: number,
    pageSize: number
  ) =>
    [
      ...offerTrackingKeys.all,
      "overview",
      dbName,
      ops,
      date,
      lastStage,
      page,
      pageSize,
    ] as const,
};

interface UseOverviewProps {
  dbName: string;
  ops: OpsFilter;
  date: string;
  lastStage: LastStageFilter;
  page: number;
  pageSize: number;
  enabled: boolean;
}

export function useOverview({
  dbName,
  ops,
  date,
  lastStage,
  page,
  pageSize,
  enabled,
}: UseOverviewProps) {
  return useQuery<OverviewResult>({
    queryKey: offerTrackingKeys.overview(dbName, ops, date, lastStage, page, pageSize),
    queryFn: async () => {
      const response = await offerTrackingService.overview({
        dbName,
        ops,
        date,
        lastStage,
        page,
        pageSize,
      });
      return response.data;
    },
    enabled: enabled && !!dbName && !!date,
    staleTime: 2 * 60 * 1000,
    placeholderData: (prev) => prev,
    retry: 1,
  });
}

export interface FieldErrors {
  telefone?: string;
  cpf?: string;
  cnpj?: string;
}

interface UseIndividualSearchProps {
  dbName: string;
  ops: string;
  /** YYYY-MM-DD — ofertas a partir desta data (exclusivo da aba individual) */
  dataInicio?: string;
}

export function useIndividualSearch({ dbName, ops, dataInicio }: UseIndividualSearchProps) {
  const [searchForm, setSearchForm] = useState<SearchFormData>({
    telefone: "",
    cpf: "",
    cnpj: "",
  });
  const [result, setResult] = useState<IndividualResult | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const searchMutation = useMutation({
    mutationFn: async (form: SearchFormData) => {
      const params: any = { dbName, ops };
      if (dataInicio) params.dataInicio = dataInicio;
      if (form.telefone.trim()) params.telefone = form.telefone.replace(/\D/g, "");
      if (form.cpf.trim()) params.cpf = form.cpf.replace(/\D/g, "");
      if (form.cnpj.trim()) params.cnpj = cleanCnpj(form.cnpj);
      const response = await offerTrackingService.individual(params);
      return response.data;
    },
    onSuccess: (data) => setResult(data),
    onError: () => setResult(null),
  });

  const validateField = (name: keyof SearchFormData, value: string) => {
    const errors = { ...fieldErrors };
    switch (name) {
      case "telefone":
        if (value && !isValidPhone(value))
          errors.telefone = "Telefone deve ter 10-11 dígitos (DDD + número)";
        else delete errors.telefone;
        break;
      case "cpf":
        if (value && !isValidCpf(value)) errors.cpf = "CPF deve ter 11 dígitos";
        else delete errors.cpf;
        break;
      case "cnpj":
        if (value && !isValidCnpj(value))
          errors.cnpj = "CNPJ deve ter 14 dígitos válidos";
        else delete errors.cnpj;
        break;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFieldChange = (field: keyof SearchFormData, value: string) => {
    let formatted = value;
    if (field === "cpf") formatted = formatCpf(value);
    else if (field === "cnpj") formatted = formatCnpj(value);
    else if (field === "telefone") formatted = formatPhone(value);
    setSearchForm((prev) => ({ ...prev, [field]: formatted }));
    setTimeout(() => validateField(field, formatted), 300);
  };

  const handleSearch = (): { valid: boolean; message?: string } => {
    const hasAny =
      searchForm.telefone.trim() || searchForm.cpf.trim() || searchForm.cnpj.trim();
    if (!hasAny)
      return { valid: false, message: "Informe CPF, CNPJ ou telefone" };
    if (Object.keys(fieldErrors).length > 0)
      return { valid: false, message: "Corrija os campos inválidos" };
    searchMutation.mutate(searchForm);
    return { valid: true };
  };

  return {
    searchForm,
    fieldErrors,
    result,
    isSearching: searchMutation.isPending,
    isError: searchMutation.isError,
    handleFieldChange,
    handleSearch,
  };
}
